/**
 * DecisionHeader - the "lead with the decision, not the data" strip
 * (doc 33, the governing principle + cross-cutting standards #1 and #10).
 *
 * Every operational page opens with this above the fold: (1) the risk posture
 * right now, (2) the single most important things to DO next, each one click
 * from the thing to fix. Charts and tables live below it. Reused by the DSPM
 * Dashboard and the Findings triage queue so a CISO and a day-1 SOC analyst
 * both read the same shape on every screen.
 */
import { type ReactNode } from 'react';
import { motion } from 'motion/react';
import { ArrowRight, ShieldCheck, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useMotionLevel } from '@/lib/motion-level';
import { cn } from '@/lib/cn';
import type { RiskBand } from '@/lib/risk-score';

export interface DecisionAction {
  id: string;
  /** Imperative, scannable: "Vault 1,240 Aadhaar in public.customers". */
  label: string;
  /** Optional second line: where / how many / why. */
  detail?: ReactNode;
  band?: RiskBand;
  onClick: () => void;
}

interface DecisionHeaderProps {
  /** 0-100 posture score (use the worst open risk, or a rolled-up posture). */
  score: number;
  band: RiskBand;
  /** Caption under the score, e.g. "Worst open risk" or "Data risk posture". */
  scoreLabel?: string;
  trend?: { direction: 'up' | 'down' | 'flat'; label: string };
  /** Up to 3 next-best-actions. Empty => an "all clear" state is shown. */
  actions: DecisionAction[];
  /** One-line plain-language summary of the posture. */
  summary?: ReactNode;
  className?: string;
}

const BAND_RING: Record<RiskBand, string> = {
  critical: 'text-sev-critical',
  high: 'text-sev-high',
  medium: 'text-sev-medium',
  low: 'text-sev-low',
};
const BAND_BG: Record<RiskBand, string> = {
  critical: 'bg-sev-critical/10 border-sev-critical/30',
  high: 'bg-sev-high/10 border-sev-high/30',
  medium: 'bg-sev-medium/10 border-sev-medium/30',
  low: 'bg-sev-low/10 border-sev-low/30',
};
const BAND_LABEL: Record<RiskBand, string> = {
  critical: 'Critical exposure',
  high: 'High exposure',
  medium: 'Moderate exposure',
  low: 'Low exposure',
};

export function DecisionHeader({
  score,
  band,
  scoreLabel = 'Worst open risk',
  trend,
  actions,
  summary,
  className,
}: DecisionHeaderProps): JSX.Element {
  const level = useMotionLevel();
  const TrendIcon = trend?.direction === 'down' ? TrendingDown : trend?.direction === 'up' ? TrendingUp : Minus;
  const top = actions.slice(0, 3);

  return (
    <motion.div
      initial={level === 'full' ? { opacity: 0, y: 8 } : { opacity: 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={level === 'full' ? { type: 'spring', stiffness: 260, damping: 26 } : { duration: 0.18 }}
      className={cn('glass-3d rounded-xl p-4 mb-5 grid gap-4 md:grid-cols-[minmax(180px,220px)_1fr] items-stretch', className)}
    >
      {/* Posture */}
      <div className={cn('rounded-lg border p-4 flex flex-col justify-center', BAND_BG[band])}>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{scoreLabel}</div>
        <div className="flex items-baseline gap-2 mt-1">
          <span className={cn('text-4xl font-semibold tabular leading-none', BAND_RING[band])}>{score}</span>
          <span className="text-xs text-muted-foreground">/ 100</span>
        </div>
        <div className={cn('text-xs font-medium mt-1.5', BAND_RING[band])}>{BAND_LABEL[band]}</div>
        {trend && (
          <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1">
            <TrendIcon className={cn('h-3 w-3', trend.direction === 'down' ? 'text-sev-low' : trend.direction === 'up' ? 'text-sev-high' : '')} />
            {trend.label}
          </div>
        )}
      </div>

      {/* Next best actions */}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
          {top.length > 0 ? 'Do this next' : 'Status'}
        </div>
        {top.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-foreground/90 h-full">
            <ShieldCheck className="h-5 w-5 text-sev-low" />
            <span>{summary ?? 'No open high-risk exposures. Keep scanning to stay current.'}</span>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {top.map((a) => (
              <button
                key={a.id}
                onClick={a.onClick}
                className={cn(
                  'group text-left rounded-lg border p-3 transition-colors hover:bg-muted/40 min-w-0',
                  a.band ? BAND_BG[a.band] : 'border-border/40',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-foreground/95 leading-snug">{a.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </div>
                {a.detail && <div className="text-[11px] text-muted-foreground mt-1 truncate">{a.detail}</div>}
              </button>
            ))}
          </div>
        )}
        {summary && top.length > 0 && (
          <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{summary}</p>
        )}
      </div>
    </motion.div>
  );
}
